<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { toast } from 'vue-sonner'
import axios from '@/api'
import BaseButton from '@/components/BaseButton.vue'
import LoadingIcon from '@/components/LoadingIcon.vue'

/**
 * Post-call editor for the lead's full tag set and stage. Loads every tag and
 * stage on the account (not just the team's preset deal menu) and saves the
 * edited set in one PUT; tags left unselected are removed from the lead.
 */
const props = defineProps<{ callId: number }>()

const emit = defineEmits<{ done: [] }>()

type Option = { id: number; name: string; color: string }

const tags = ref<Option[]>([])
const stages = ref<Option[]>([])
const selectedTagIds = ref<number[]>([])
const stageId = ref<number | null>(null)
const search = ref('')
const isLoading = ref(true)
const loadFailed = ref(false)
const isSaving = ref(false)

// Long tag lists get a filter; short ones fit on screen as-is.
const SEARCH_THRESHOLD = 12

const filteredTags = computed(() => {
  const q = search.value.trim().toLowerCase()
  return q ? tags.value.filter((t) => t.name.toLowerCase().includes(q)) : tags.value
})

const toggleTag = (id: number) => {
  selectedTagIds.value = selectedTagIds.value.includes(id)
    ? selectedTagIds.value.filter((t) => t !== id)
    : [...selectedTagIds.value, id]
}

const load = async () => {
  isLoading.value = true
  loadFailed.value = false
  try {
    const { data } = await axios.get(`/apps/webrtc/${props.callId}/lead-tags`)
    tags.value = data.tags
    stages.value = data.stages
    selectedTagIds.value = data.lead_tag_ids
    stageId.value = data.stage_id
  } catch {
    loadFailed.value = true
  } finally {
    isLoading.value = false
  }
}

onMounted(load)

const save = async () => {
  isSaving.value = true
  try {
    await axios.put(`/apps/webrtc/${props.callId}/lead-tags`, {
      tag_ids: selectedTagIds.value,
      stage_id: stageId.value,
    })
  } catch {
    toast.error("Couldn't save the tags and stage. Please try again.")
    return
  } finally {
    isSaving.value = false
  }
  emit('done')
}

const formControl =
  'block w-full h-[34px] px-[12px] py-[6px] text-[14px] leading-[1.42857143] text-[#555] ' +
  'bg-white border border-[#ccc] rounded-[4px] shadow-[inset_0_1px_1px_rgba(0,0,0,0.075)] ' +
  'focus:border-[#66afe9] focus:outline-none'
const label = 'block text-left text-[13px] font-bold text-[#073b4c] mb-[6px]'
const chip =
  'inline-flex items-center gap-[4px] m-[3px] px-[10px] py-[5px] text-[13px] leading-none ' +
  'rounded-full border cursor-pointer select-none touch-manipulation'
</script>

<template>
  <div class="mt-[10px] mb-[30px] px-4">
    <h3 class="text-center font-normal mb-[20px] mt-0">Tags & Stage</h3>

    <div v-if="isLoading" class="flex justify-center py-[30px]"><LoadingIcon /></div>

    <p v-else-if="loadFailed" class="text-center text-[14px] text-[#666]">
      Couldn't load this lead's tags and stage.
      <button type="button" class="underline cursor-pointer text-[#337ab7]" @click="load">
        Try again
      </button>
    </p>

    <template v-else>
      <label for="lead-stage" :class="label">Stage</label>
      <select id="lead-stage" v-model="stageId" :class="formControl">
        <option :value="null">No stage</option>
        <option v-for="stage in stages" :key="stage.id" :value="stage.id">
          {{ stage.name }}
        </option>
      </select>

      <div class="mt-[18px]" :class="label">Tags</div>
      <input
        v-if="tags.length > SEARCH_THRESHOLD"
        v-model="search"
        type="search"
        placeholder="Search tags..."
        class="mb-[6px]"
        :class="formControl"
      />
      <p v-if="!tags.length" class="text-left text-[13px] text-[#666]">
        No tags on this account yet.
      </p>
      <div class="max-h-[180px] overflow-y-auto text-left -mx-[3px]">
        <button
          v-for="tag in filteredTags"
          :key="tag.id"
          type="button"
          data-test="tag-chip"
          :class="chip"
          :aria-pressed="selectedTagIds.includes(tag.id)"
          :style="
            selectedTagIds.includes(tag.id)
              ? { backgroundColor: tag.color, borderColor: tag.color, color: '#fff' }
              : { borderColor: '#ccc', color: '#073b4c' }
          "
          @click="toggleTag(tag.id)"
        >
          <i v-if="selectedTagIds.includes(tag.id)" class="material-icons text-[14px]!">check</i>
          <span
            v-else
            class="inline-block w-[8px] h-[8px] rounded-full"
            :style="{ backgroundColor: tag.color }"
          />
          {{ tag.name }}
        </button>
      </div>
    </template>

    <div class="flex justify-end mt-[20px]">
      <BaseButton type="button" variant="link" size="lg" :disabled="isSaving" @click="emit('done')"
        >Skip</BaseButton
      >
      <BaseButton
        v-if="!isLoading && !loadFailed"
        type="button"
        variant="primary"
        size="lg"
        :disabled="isSaving"
        @click="save"
      >
        Save
      </BaseButton>
    </div>
  </div>
</template>
